import React, { useState } from 'react';

import { Alert, Button, Modal } from '@shared/components/bootstrap';

import { client } from '@admin/libraries/apollo';
import { Account__State, Status, useCancelAccountDueToBankruptcyMutation } from '@admin/schema';
interface ICancelAccountDueToBankruptcyProps {
  accountID: string;
  accountNumber: string;
  accountState: Account__State | string;
  onCanceled(): void;
}

const CancelDueToBankruptcyModal: React.FC<
  ICancelAccountDueToBankruptcyProps & {
    onClose(): void;
  }
> = ({ accountID, accountNumber, accountState, onCanceled, onClose }) => {
  const [error, setError] = useState<string | undefined>();

  const [cancelDueToBankruptcy, { loading }] = useCancelAccountDueToBankruptcyMutation({ client });

  const onConfirm = async () => {
    const response = await cancelDueToBankruptcy({ variables: { accountID } });
    if (response && response.data?.cancelAccountDueToBankruptcy) {
      if (response.data.cancelAccountDueToBankruptcy.error) {
        setError(
          `We were unable to cancel this account. Please contact tech support. ${response.data.cancelAccountDueToBankruptcy.error}`,
        );
      } else if (response.data.cancelAccountDueToBankruptcy.status === Status.Ok) {
        onCanceled();
        onClose();
      }
    }
  };

  return (
    <Modal onClose={onClose}>
      <Modal.Content>
        <Modal.Header>
          <Modal.Title>Cancel Account #{accountNumber} Due to Bankruptcy</Modal.Title>
          <Modal.Close close={onClose} />
        </Modal.Header>
        <Modal.Body>
          {error && (
            <Alert style="danger">
              <strong>Error: </strong>
              {error}
            </Alert>
          )}
          <div>
            <p>Are you sure you want to cancel this account due to bankruptcy?</p>
            <p>This will:</p>
            <ul>
              <li>Turn off all automated billing</li>
              <li>Cancel all subscriptions</li>
              <li>Stop any automated outreach about overdue bills</li>
              <li>Put the account into a canceled state</li>
            </ul>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={onClose} disabled={loading} size="lg" kind="danger">
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={loading} size="lg" kind="primary">
            Confirm Bankruptcy Cancellation
          </Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
};

export const CancelDueToBankruptcyButton: React.FC<ICancelAccountDueToBankruptcyProps> = ({
  accountID,
  accountNumber,
  accountState,
  onCanceled,
}) => {
  const [showModal, setShowModal] = useState<boolean>(false);

  // Check for both uppercase SUSPENDED and lowercase suspended. This is to support passing the string value from the angular template.
  const isSuspended = accountState === Account__State.Suspended || String(accountState).toLowerCase() === 'suspended';

  if (!isSuspended) {
    return null;
  }

  return (
    <>
      <Button kind="danger" onClick={() => setShowModal(true)}>
        Cancel Due to Bankruptcy
      </Button>
      {showModal && (
        <CancelDueToBankruptcyModal
          accountID={accountID}
          accountNumber={accountNumber}
          accountState={accountState}
          onCanceled={onCanceled}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
};
